import type { ApiFlowsResponseType, FlowsType } from "@/types/entities/flows";
import axios from "@/utils/axios";
import type { ApiNode } from "@/workflow_api/types/core";

import {
  keepPreviousData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import type {
  GetFlowsFilters,
  MainFlowsResponse,
  UpdateFlowData,
} from "./flows.types";

export {
  useRetryDocumentGeneration,
  useGetNodeWithCalculatedFieldData,
  useGetFlowExecutionsAsInfinite,
  useGetFlowExecutions,
  useGetExecution,
} from "./flows.executions.api";
export {
  useDuplicateFlow,
  usePerformFlowConnectionsCheck,
  useSendFlowToApproval,
  useUpdateFlowRevision,
} from "./flows.mutations.api";

export const useGetFlowsByProcessId = (
  processId: string | undefined,
  params: GetFlowsFilters,
  interval = 0,
  enabled?: boolean,
) => {
  const queryParams = {
    page: params.page || 1,
    per_page: params.perPage || 10,
    filter: {
      process_id: processId,
      ...(params.search && { search: params.search }),
      ...(params.status && { status: params.status }), // Include status filter
      ...(params.parent && { parent: params.parent }),
    },
    ...(params.order_by && { order_by: params.order_by }),
  };

  return useQuery({
    queryKey: ["flows", queryParams],
    queryFn: async (): Promise<ApiFlowsResponseType> => {
      const response = await axios.get(`/admin/v1/flows`, {
        params: {
          ...queryParams,
          relationships: ["childFlows"].join(","),
        },
      });
      return response.data;
    },
    ...(interval > 0 && { refetchInterval: interval }),
    enabled: !!processId && enabled, // Only run the query if processId is provided and enabled is true
    placeholderData: keepPreviousData,
  });
};

export const useGetFlowsByProcessIdAsInfinite = (
  processId: string | undefined,
  params: Omit<GetFlowsFilters, "page">,
) => {
  const queryParams = {
    per_page: params.perPage || 10,
    filter: {
      process_id: processId,
      ...(params.search && { search: params.search }),
      ...(params.status && { status: params.status }), // Include status filter
    },
    ...(params.order_by && { order_by: params.order_by }),
  };
  return useInfiniteQuery({
    queryKey: ["flows-infinite", queryParams],
    queryFn: async ({ pageParam = 1 }): Promise<ApiFlowsResponseType> => {
      const response = await axios.get(`/admin/v1/flows`, {
        params: {
          ...queryParams,
          page: pageParam,
          relationships: ["childFlows", "renderableNodes"].join(","),
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = (
        lastPage as {
          meta: { current_page: number; last_page: number };
        }
      ).meta;
      if (current_page < last_page) {
        return current_page + 1;
      }
    },
    enabled: !!processId, // Only run the query if processId is provided
    initialPageParam: 1,
  });
};

export const useGetMainFlowsByProcessId = (
  processId?: string,
  canViewFlows?: boolean,
) => {
  return useQuery({
    queryKey: ["main-flows", processId],
    queryFn: async (): Promise<MainFlowsResponse> => {
      const response = await axios.get(`/admin/v1/flows`, {
        params: {
          per_page: 1000,
          filter: {
            process_id: processId,
            main_flow: true,
          },
          order_by: "!created_at",
          relationships: "revisions,revisions.comments",
        },
      });
      return response.data;
    },
    enabled: !!processId && canViewFlows,
  });
};

export const useStoreFlow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FlowsType) => {
      const response = await axios.post("/admin/v1/flows", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["flows"],
      });
    },
    onError: (error) => {
      // Handle error here, e.g., show a notification
      console.error("Error creating flow:", error);
    },
  });
};

export const useUpdateFlow = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    //mutationFn: async (data: Partial<FlowsType>) => {
    mutationFn: async (data: Partial<UpdateFlowData>) => {
      const response = await axios.put(`/admin/v1/flows/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-flow-detail", id],
      });
    },
    onError: (error) => {
      // Handle error here, e.g., show a notification
      console.error("Error updating flow:", error);
    },
  });
};

export const useUpdateFlowStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { id: string; status: FlowsType["status"] }) => {
      const response = await axios.patch(`/admin/v1/flows/${params.id}`, {
        status: params.status,
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-flow-detail", variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["flows"],
      });
      queryClient.invalidateQueries({
        queryKey: ["main-flows"],
      });
    },
    onError: (error) => {
      // Handle error here, e.g., show a notification
      console.error("Error updating flow status:", error);
    },
  });
};

export const useArchiveFlow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (flowId: string) => {
      const response = await axios.put(`/admin/v1/flows/${flowId}`, {
        status: "DEPRECATED",
      });
      return response.data;
    },
    onSuccess: (_, flowId) => {
      queryClient.invalidateQueries({ queryKey: ["main-flows"] });
      queryClient.invalidateQueries({ queryKey: ["flows"] });
      queryClient.invalidateQueries({
        queryKey: ["admin-flow-detail", flowId],
      });
    },
  });
};

const getFlow = async (
  id: string,
  includeNodes: boolean = true,
): Promise<{
  data: ApiFlowsResponseType["data"][number] & { nodes: ApiNode[] };
}> => {
  const response = await axios.get(
    `/admin/v1/flows/${id}?relationships=${[
      ...(includeNodes
        ? ["nodes", "nodes.edge", "nodes.edges", "nodes.targetConnections"]
        : []),
      "revisions",
      "revisions.comments",
    ].join(",")}`,
  );
  return response.data;
};

export const useGetFlow = (
  id: string,
  includeNodes: boolean = true,
  canViewFlow?: boolean,
) => {
  return useQuery({
    queryKey: ["admin-flow-detail", id, includeNodes],
    enabled: !!id && canViewFlow,
    staleTime: 500,
    queryFn: async (): Promise<{
      data: ApiFlowsResponseType["data"][number] & { nodes: ApiNode[] };
    }> => {
      const flow = await getFlow(id, includeNodes);
      return flow;
    },
  });
};

export const useDeleteFlow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (flowId: string) => {
      const response = await axios.delete(`/admin/v1/flows/${flowId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["flows"],
      });
      queryClient.invalidateQueries({
        queryKey: ["main-flows"],
      });
    },
    onError: (error) => {
      // Handle error here, e.g., show a notification
      console.error("Error deleting flow:", error);
    },
  });
};

export const createFlowDetailsBreadcrumb = (
  queryClient: QueryClient,
  id: string,
) => {
  return async () => {
    const flowId = id;
    try {
      const queryKey = ["admin-flow-detail", flowId];

      const flow = await queryClient.ensureQueryData({
        queryKey,
        queryFn: async () => getFlow(flowId),
        staleTime: 5 * 60 * 1000,
      });

      return { data: flow.data };
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      throw (
        axiosError ?? {
          code: 500,
          message: "Unknown error",
        }
      );
    }
  };
};

/**
 * Minimal flow data fetch by id
 * @param flowId
 * @returns
 */
export const useGetFlowById = (flowId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["flow-by-id", flowId],
    enabled: enabled && !!flowId,
    staleTime: 500,
    queryFn: async (): Promise<{
      data: ApiFlowsResponseType["data"][number] & { nodes: ApiNode[] };
    }> => {
      const response = await axios.get(`/admin/v1/flows/${flowId}`);
      return response.data;
    },
  });
};

export const useGetFlows = (params: {
  page?: number;
  perPage?: number;
  search?: string;
  status?: string;
  order_by?: string;
}) => {
  const queryParams = {
    page: params.page || 1,
    per_page: params.perPage || 10,
    ...(params.search && { search: params.search }),
    ...(params.status && { status: params.status }),
    ...(params.order_by && { order_by: params.order_by }),
  };
  return useQuery({
    queryKey: ["api-flows", params],
    queryFn: async (): Promise<ApiFlowsResponseType> => {
      const response = await axios.get(`/admin/v1/flows`, {
        params: queryParams,
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 30, // 30 segundos (opcional)
  });
};

export const useGetFlowsAsInfinite = (
  params: Omit<GetFlowsFilters, "page">,
  enabled: boolean = true,
) => {
  const queryParams = {
    per_page: params.perPage || 20,
    filter: {
      ...(params.search && { search: params.search }),
      ...(params.status && { status: params.status }),
      ...(params.parent && { parent: params.parent }),
    },
    ...(params.order_by && { order_by: params.order_by }),
  };
  return useInfiniteQuery({
    queryKey: ["api-flows-infinite", queryParams],
    queryFn: async ({ pageParam = 1 }): Promise<ApiFlowsResponseType> => {
      const response = await axios.get(`/admin/v1/flows`, {
        params: {
          ...queryParams,
          page: pageParam,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage: ApiFlowsResponseType) => {
      const { current_page, last_page } = lastPage.meta;
      if (current_page < last_page) {
        return current_page + 1;
      }
    },
    initialPageParam: 1,
    staleTime: 1000 * 30,
    enabled,
    placeholderData: keepPreviousData,
  });
};
